import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import InboxDocumentViewer from "../Inbox/components/InboxDocumentViewer";
import { getPresignedDocumentUrl } from "../../components/Document/DocumentUtils";
import {
  boardAppealAttachmentUrl,
  decideBoardAppealAPI,
  fetchBoardAppealCardAPI,
  fetchBoardAppealCorrespondenceAPI,
  fetchBoardAppealFilesAPI,
  pickBoardAppealDecisionAPI,
  replyBoardAppealCorrespondenceAPI,
  fetchBoardAppealListAPI,
  refreshBoardAppealsAPI,
} from "../../service/boardAppeals";
import { setBoardAppealPendingCount } from "../../slice/BoardAppealSlice";
import AppealQueue from "./components/AppealQueue";
import AppealFacts, { attachmentId } from "./components/AppealFacts";
import AppealDecision from "./components/AppealDecision";
import AppealCorrespondence from "./components/AppealCorrespondence";
import {
  Banner,
  CenterColumn,
  Muted,
  NoDocument,
  PageWrapper,
  RightRail,
  ToolsSlot,
  ViewerSlot,
} from "./BoardAppealsStyled";

const reviewIdFrom = (search) => {
  const raw = new URLSearchParams(search).get("review");
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * Board appeal review: the pile on the left, the Board decision in the
 * middle, the call and the emails about the referral on the right.
 *
 * The pile holds every Court of Appeals intake waiting in attorney review
 * in Litify, plus any Board decision a staff member sent over, folded
 * into one card per client.
 */
const BoardAppeals = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const [scope, setScope] = useState("pending");
  const [queue, setQueue] = useState({ items: [], counts: {}, lastSync: null });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedId, setSelectedId] = useState(() => reviewIdFrom(location.search));
  const reviewIdRef = useRef(selectedId);
  reviewIdRef.current = selectedId;
  const [card, setCard] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [viewedId, setViewedId] = useState(null);
  const [mail, setMail] = useState({ items: [], loading: false, error: null });
  const [files, setFiles] = useState({ reviewId: null, attachments: [], copies: [] });
  const [picking, setPicking] = useState(false);
  const [pickError, setPickError] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const attachmentRef = useRef(null);
  attachmentRef.current = attachment;

  const [busy, setBusy] = useState(false);
  const [decideError, setDecideError] = useState(null);
  const [flash, setFlash] = useState(null);

  const writeSearch = useCallback(
    (mutate) => {
      const { pathname, search } = locationRef.current;
      const params = new URLSearchParams(search);
      mutate(params);
      const next = params.toString();
      navigate(`${pathname}${next ? `?${next}` : ""}`, { replace: true });
    },
    [navigate]
  );

  const select = useCallback(
    (id) => {
      setSelectedId(id);
      setDecideError(null);
      writeSearch((params) => {
        if (id) params.set("review", String(id));
        else params.delete("review");
      });
    },
    [writeSearch]
  );

  const loadQueue = useCallback(
    async (which) => {
      setQueueLoading(true);
      setQueueError(null);
      try {
        const { data } = await fetchBoardAppealListAPI(which);
        const next = {
          items: data?.items || [],
          counts: data?.counts || {},
          lastSync: data?.lastSync ?? null,
        };
        setQueue(next);
        dispatch(setBoardAppealPendingCount(next.counts.pending ?? 0));
        return next.items;
      } catch (error) {
        setQueueError(error.message);
        return [];
      } finally {
        setQueueLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    let cancelled = false;
    loadQueue(scope).then((items) => {
      if (cancelled) return;
      const current = reviewIdFrom(locationRef.current.search);
      if (!current && items.length) select(items[0].id);
    });
    return () => {
      cancelled = true;
    };
  }, [scope, loadQueue, select]);

  useEffect(() => {
    if (!selectedId) {
      setCard(null);
      setViewedId(null);
      return undefined;
    }
    let cancelled = false;
    setCardError(null);
    fetchBoardAppealCardAPI(selectedId)
      .then(({ data }) => {
        if (cancelled) return;
        setCard(data);
        setViewedId(data?.documentId || null);
      })
      .catch((error) => {
        if (cancelled) return;
        setCard(null);
        setViewedId(null);
        setCardError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const loadFiles = useCallback((id) => {
    setFiles({ reviewId: null, attachments: [], copies: [] });
    return fetchBoardAppealFilesAPI(id)
      .then(({ data }) => {
        if (reviewIdRef.current !== id) return;
        setFiles({
          reviewId: id,
          attachments: data?.attachments || [],
          copies: data?.copies || [],
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPickError(null);
    if (selectedId) loadFiles(selectedId);
    else setFiles({ reviewId: null, attachments: [], copies: [] });
  }, [selectedId, loadFiles]);

  useEffect(() => {
    if (!selectedId) {
      setMail({ items: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setMail({ items: [], loading: true, error: null });
    fetchBoardAppealCorrespondenceAPI(selectedId)
      .then(({ data }) => {
        if (!cancelled) setMail({ items: data?.items || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setMail({ items: [], loading: false, error: error.message });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const reply = async (email, message) => {
    const { data } = await replyBoardAppealCorrespondenceAPI(selectedId, email.id, message);
    setMail({ items: data?.items || [], loading: false, error: null });
    setFlash(`Reply sent to ${message.to.join(", ")}.`);
  };

  const changeScope = (next) => {
    if (next === scope) return;
    setFlash(null);
    select(null);
    setScope(next);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await refreshBoardAppealsAPI();
      await loadQueue(scope);
      if (data?.errors) setQueue((q) => ({ ...q, lastSync: data }));
    } catch (error) {
      setQueueError(error.message);
    } finally {
      setRefreshing(false);
    }
  };

  const decide = async (action, note, boardDate) => {
    if (!card) return;
    setBusy(action);
    setDecideError(null);
    try {
      const { data } = await decideBoardAppealAPI(card.id, {
        action,
        note,
        boardDate,
      });
      const verb = action === "signUp" ? "signed up" : "turned down";
      setFlash(
        `${card.clientName || "This client"}: ${verb}.` +
          (data?.litifyResult ? ` ${data.litifyResult}` : "")
      );
      if (scope === "pending") {
        const rest = queue.items.filter((r) => r.id !== card.id);
        const at = queue.items.findIndex((r) => r.id === card.id);
        const pending = Math.max(0, (queue.counts.pending ?? 1) - 1);
        setQueue((q) => ({
          ...q,
          items: rest,
          counts: { ...q.counts, pending },
        }));
        dispatch(setBoardAppealPendingCount(pending));
        const next = rest[Math.min(Math.max(at, 0), rest.length - 1)];
        select(next ? next.id : null);
      } else {
        setCard(data);
      }
    } catch (error) {
      setDecideError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const pickDecision = async (choice) => {
    if (!card || card.id !== selectedId || files.reviewId !== card.id) return false;
    setPicking(true);
    setPickError(null);
    try {
      const { data } = await pickBoardAppealDecisionAPI(card.id, choice);
      setCard(data);
      setViewedId(data?.documentId || null);
      return true;
    } catch (error) {
      setPickError(error.message);
      return false;
    } finally {
      setPicking(false);
    }
  };

  const decisionId = card?.documentId || null;
  const viewedEmail = mail.items.find((m) => m.documentId === viewedId);
  const viewedAttachment = attachment && attachment.id === viewedId ? attachment : null;
  const viewedName =
    viewedAttachment?.name ||
    viewedEmail?.documentName ||
    files.copies.find((f) => f.id === viewedId)?.name ||
    card?.documentName ||
    "";

  const openFile = useCallback((id) => setViewedId(id), []);

  // An email's attachment isn't a WHG Docs document of its own, so the
  // viewer is pointed at it under a made-up id and fetches it from the card.
  const openAttachment = useCallback(
    (email, att) => {
      const id = attachmentId({ emailId: email.id, index: att.index });
      setAttachment({
        id,
        url: boardAppealAttachmentUrl(selectedId, email.id, att.index),
        name: att.name,
        contentType: att.contentType || "",
      });
      setViewedId(id);
    },
    [selectedId]
  );

  const resolveViewerUrl = useCallback(async (documentId) => {
    const att = attachmentRef.current;
    if (att && att.id === documentId) {
      return {
        url: att.url,
        isDirect: false,
        withCredentials: true,
        fileName: att.name,
        contentType: att.contentType,
      };
    }
    return getPresignedDocumentUrl(documentId);
  }, []);

  return (
    <PageWrapper>
      <AppealQueue
        scope={scope}
        onScope={changeScope}
        items={queue.items}
        counts={queue.counts}
        lastSync={queue.lastSync}
        loading={queueLoading}
        error={queueError}
        selectedId={selectedId}
        onSelect={select}
        refreshing={refreshing}
        onRefresh={refresh}
      />
      <CenterColumn>
        {flash ? (
          <Banner $tone="ready" role="status" style={{ margin: 0 }}>
            {flash}
          </Banner>
        ) : null}
        {cardError ? (
          <Banner role="alert" style={{ margin: 0 }}>
            {cardError}
          </Banner>
        ) : null}
        {card ? (
          <AppealFacts
            key={card.id}
            card={card}
            viewedId={viewedId}
            onViewFile={setViewedId}
            decisionId={decisionId}
            files={files.reviewId === card.id ? files : null}
            onOpenAttachment={(att) => openAttachment({ id: att.emailId }, att)}
            onPick={pickDecision}
            picking={picking}
            pickError={pickError}
          />
        ) : null}
        <ViewerSlot>
          {card && !viewedId ? (
            <NoDocument>
              <strong>No Board decision in this client&apos;s file.</strong>
              <span>
                Litify has the intake waiting, but nothing in WHG Docs looks
                like the Board&apos;s decision.
              </span>
            </NoDocument>
          ) : !card ? (
            <NoDocument>
              <Muted>
                {queue.items.length
                  ? "Pick a client on the left."
                  : "Nothing to show."}
              </Muted>
            </NoDocument>
          ) : (
            <InboxDocumentViewer
              documentId={viewedId}
              documentName={viewedName}
              resolveUrl={resolveViewerUrl}
              editable={Boolean(decisionId) && viewedId === decisionId && Boolean(card?.decisionOrigin)}
            />
          )}
        </ViewerSlot>
      </CenterColumn>
      <RightRail>
        {card ? (
          <AppealDecision
            card={card}
            busy={busy}
            error={decideError}
            onDecide={decide}
          />
        ) : null}
        {card ? (
          <ToolsSlot>
            <AppealCorrespondence
              reviewId={card.id}
              items={mail.items}
              loading={mail.loading}
              error={mail.error}
              activeId={viewedId}
              onOpen={openFile}
              onOpenAttachment={openAttachment}
              onReply={reply}
            />
          </ToolsSlot>
        ) : null}
      </RightRail>
    </PageWrapper>
  );
};

export default BoardAppeals;
